import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Avatar from '../../components/Avatar';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as usersApi from '../../api/users.api';
import * as analytics from '../../api/analytics.api';
import { errorMessage } from '../../api/client';
import { formatDate } from '../../utils/format';

function Vendors() {
  const [rows, setRows] = useState([]);
  const [perf, setPerf] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      usersApi.listAdminUsers({ role: 'VENDOR' }),
      analytics.vendorsReport().catch(() => ({ items: [] }))
    ])
      .then(([users, vReport]) => {
        if (cancelled) return;
        setRows(users || []);
        const map = {};
        for (const it of vReport?.items || []) map[String(it.vendorId)] = it;
        setPerf(map);
      })
      .catch((e) => { if (!cancelled) setErr(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Clinics" subtitle="Partner clinics, their licensing status, and performance." />
      <DataTable
        rows={rows}
        searchPlaceholder="Search clinics…"
        columns={[
          {
            key: 'name',
            header: 'Clinic',
            render: (r) => (
              <div className="row">
                <Avatar name={r.name} />
                <div style={{ minWidth: 0, maxWidth: 220 }}>
                  <div className="cell-strong cell-truncate" title={r.name || ''}>{r.name}</div>
                  <div className="muted text-xs cell-truncate" title={r.email || ''}>{r.email}</div>
                </div>
              </div>
            )
          },
          { key: 'phone', header: 'Phone', render: (r) => r.phone || <span className="muted">—</span> },
          { key: 'assigned', header: 'Assigned', render: (r) => (perf[String(r._id)]?.assigned ?? 0) },
          { key: 'completed', header: 'Completed', render: (r) => (perf[String(r._id)]?.completed ?? 0) },
          {
            key: 'rate',
            header: 'Rate',
            render: (r) => {
              const rate = perf[String(r._id)]?.completionRate;
              return rate != null ? `${Math.round(rate * 100)}%` : <span className="muted">—</span>;
            }
          },
          {
            key: 'avgTurnaround',
            header: 'Avg turnaround',
            render: (r) => {
              const v = perf[String(r._id)]?.avgTurnaroundHours;
              return v != null ? `${v}h` : <span className="muted">—</span>;
            }
          },
          {
            key: 'approval',
            header: 'Approval',
            render: (r) => {
              const s = r.clinicProfile?.approvalStatus || 'PENDING';
              const tone = s === 'APPROVED' ? 'success' : s === 'PENDING' ? 'warning' : 'danger';
              return <span className={`badge ${tone}`}>{s.toLowerCase()}</span>;
            }
          },
          { key: 'isActive', header: 'Status', render: (r) => r.isActive ? <span className="badge success">Active</span> : <span className="badge neutral">Disabled</span> },
          {
            key: 'sharePct', header: 'Share %',
            render: (r) => {
              const pct = r.clinicProfile?.clinicSharePct;
              return pct != null ? <span className="badge neutral">{pct}%</span> : <span className="muted">—</span>;
            }
          },
          { key: 'createdAt', header: 'Joined', render: (r) => <span className="muted">{formatDate(r.createdAt)}</span> },
          {
            key: 'payoutSettings', header: '',
            render: (r) => (
              <Link className="btn btn-ghost btn-sm" to={`/admin/clinics/${r._id}/payout-settings`}>
                <Wallet size={14} /> Payout
              </Link>
            )
          }
        ]}
        emptyTitle="No vendors yet"
        emptyMessage="Register a vendor account, then assign them to paid orders."
      />
      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}
    </>
  );
}

export default Vendors;
